import { useRef, useEffect } from 'react'
import { buildEditorFromExtensions, defineExtension } from '@lexical/extension'
import { RichTextExtension } from '@lexical/rich-text'
import { ListExtension, CheckListExtension } from '@lexical/list'
import DefaultNodes from '@/lib/lexical/nodes'
import DefaultTheme from '@/lib/lexical/theme'
import { LinkExtension } from '@lexical/link'
import { SNLinkImportExtension } from '@/lib/lexical/exts/link-import'

const DEFAULT_EXTENSIONS = []
const DEFAULT_NAME = 'sn-headless-bridge'
const DEFAULT_NAMESPACE = 'sn-rich'

/** creates a headless Lexical editor for use as a processing bridge */
export function createHeadlessBridge (opts = {}) {
  const {
    nodes = DefaultNodes,
    theme = DefaultTheme,
    extensions = DEFAULT_EXTENSIONS,
    name = DEFAULT_NAME,
    namespace = DEFAULT_NAMESPACE
  } = opts

  return buildEditorFromExtensions(
    defineExtension({
      onError: (error) => console.error('editor bridge has encountered an error:', error),
      name,
      dependencies: [
        RichTextExtension,
        ListExtension,
        CheckListExtension,
        LinkExtension,
        // hardened anchor import: relative hrefs from pasted foreign DOM
        // (x.com) must not re-target to our origin (404s)
        SNLinkImportExtension,
        ...extensions
      ],
      nodes,
      theme,
      namespace
    })
  )
}

/** run a function with a disposable headless bridge
 * @param fn - function to run with the bridge
 * @param opts - optional configuration for the bridge editor
 * @see createHeadlessBridge for available options
 *
 * @returns result of the function
 */
export function withDisposableBridge (fn, opts = {}) {
  return (...args) => {
    // create a fresh bridge for this call
    const bridge = createHeadlessBridge(opts)
    try {
      return fn(bridge, ...args)
    } finally {
      // always dispose of it after use
      bridge.dispose()
    }
  }
}

/**
 * shared hook that creates and manages a headless bridge editor
 * @param {Object} [opts] - optional configuration for the bridge editor
 * @param {Array} [opts.nodes] - custom nodes to use (defaults to DefaultNodes)
 * @param {Object} [opts.theme] - theme configuration (defaults to DefaultTheme)
 * @param {Array} [opts.extensions] - additional extensions to use (defaults to [])
 * @param {string} [opts.name] - name of the bridge editor (defaults to 'sn-headless-bridge')
 * @param {string} [opts.namespace] - namespace of the bridge editor (defaults to 'sn-rich')
 * @returns {React.RefObject} ref to the bridge editor instance
 */
export default function useHeadlessBridge (opts = {}) {
  const bridge = useRef(null)

  useEffect(() => {
    if (!bridge.current) {
      bridge.current = createHeadlessBridge(opts)
    }
    return () => {
      if (bridge.current) {
        bridge.current.dispose()
        bridge.current = null
      }
    }
  }, [])

  return bridge
}
